import axios, { type AxiosError } from 'axios';
import { csrfCookieName, type ApiError } from '@evolfin/shared';

/**
 * Client HTTP unico dell'app. Qui e solo qui vivono:
 *  · `withCredentials` (invia il cookie di sessione httpOnly);
 * · l'interceptor CSRF (double-submit) — configurato una volta
 *    sola, niente token per-form;
 *  · la mappatura degli errori nella forma `ApiError` di `@evolfin/shared`.
 */
export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  withCredentials: true,
  // axios legge il cookie CSRF e lo rispedisce in header da sé.
  xsrfCookieName: csrfCookieName(import.meta.env.PROD),
  xsrfHeaderName: 'x-csrf-token',
});

/**
 * Il cookie CSRF si ottiene una volta all'avvio dell'app; da lì in poi
 * l'interceptor di axios fa tutto da solo.
 */
export async function inizializzaCsrf(): Promise<void> {
  await http.get('/api/csrf');
}

/** Errore applicativo con il payload dell'API già estratto. */
export class EvolfinApiError extends Error {
  constructor(readonly dettagli: ApiError) {
    super(dettagli.message);
    this.name = 'EvolfinApiError';
  }

  /** Errori di validazione per campo, pronti da agganciare al form. */
  get erroriCampo(): Record<string, string[]> {
    return this.dettagli.fieldErrors ?? {};
  }
}

http.interceptors.response.use(
  (risposta) => risposta,
  (errore: AxiosError<ApiError>) => {
    const dati = errore.response?.data;
    if (dati?.code) return Promise.reject(new EvolfinApiError(dati));

    return Promise.reject(
      new EvolfinApiError({
        statusCode: errore.response?.status ?? 0,
        code: errore.code ?? 'NETWORK_ERROR',
        message: errore.response ? errore.message : 'Server non raggiungibile',
      }),
    );
  },
);
